import React, { useEffect, useState } from "react";
import { Star, Moon, Sun, ArrowUpRight, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import Markdown from "@/components/Markdown";

export default function AstrologyService() {
  const [zodiacs, setZodiacs] = useState([]);
  const [selected, setSelected] = useState("aries");
  const [reading, setReading] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get("/zodiacs").then((r) => setZodiacs(r.data)); }, []);

  const fetchHoroscope = async (z) => {
    setSelected(z); setLoading(true); setReading("");
    try {
      const { data } = await api.post("/horoscope", { zodiac: z, period: "daily" });
      setReading(data.reading);
    } catch (e) { setReading("Try again in a moment 🙏"); }
    setLoading(false);
  };

  return (
    <div className="relative min-h-screen">
      {/* cosmic hero */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 starfield opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A0C10] via-[#050507] to-[#050507]" />
        <div className="relative max-w-7xl mx-auto px-6 pt-16 pb-24">
          <div className="text-xs uppercase tracking-[0.35em] text-[#D4AF37] mb-4">Jyotish Shastra</div>
          <h1 className="font-serif text-5xl md:text-7xl leading-[0.95] tracking-tighter max-w-4xl">
            The eternal <span className="italic gold-gradient">Vedic Astrology.</span>
          </h1>
          <p className="mt-6 max-w-xl text-[#A0A4AB] leading-relaxed">
            Rooted in 5,000-year-old Parashari & KP systems — decoded by AI, verified by masters.
          </p>
        </div>
      </div>

      {/* Zodiac constellation */}
      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-3">Pick your Rashi</div>
        <h2 className="font-serif text-3xl md:text-4xl tracking-tight mb-10">Read the sky <span className="italic gold-gradient">today.</span></h2>

        <div className="grid grid-cols-3 md:grid-cols-6 gap-3 mb-10">
          {zodiacs.map((z) => (
            <button
              key={z.key}
              data-testid={`astro-zodiac-${z.key}`}
              onClick={() => fetchHoroscope(z.key)}
              className={`group p-5 rounded-xl border transition-all duration-300 text-center ${
                selected === z.key
                  ? "border-[#D4AF37] bg-[#D4AF37]/10 scale-105"
                  : "border-white/10 hover:border-[#D4AF37]/50 bg-[#0A0C10]"
              }`}
            >
              <div className="text-3xl text-[#D4AF37]">{z.symbol}</div>
              <div className="font-serif text-base mt-2">{z.name}</div>
              <div className="font-deva text-[11px] text-[#A0A4AB] mt-0.5">{z.hi}</div>
            </button>
          ))}
        </div>

        <div className="p-8 rounded-2xl bg-gradient-to-br from-[#12151C] to-[#0A0C10] border border-[#D4AF37]/20 min-h-[240px]">
          {loading && (
            <div className="flex items-center gap-3 text-[#A0A4AB]">
              <div className="w-4 h-4 rounded-full border-2 border-[#D4AF37] border-t-transparent animate-spin" />
              Casting today's sky…
            </div>
          )}
          {!loading && !reading && (
            <div className="text-[#A0A4AB]">Select your rashi above to reveal today's celestial forecast.</div>
          )}
          {!loading && reading && <Markdown text={reading} />}
        </div>
      </div>

      {/* Book Astrologer CTA */}
      <div className="border-t border-white/10 bg-[#0A0C10]">
        <div className="max-w-7xl mx-auto px-6 py-20 grid md:grid-cols-2 items-center gap-10">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-3">Personal Consult</div>
            <h3 className="font-serif text-3xl md:text-4xl leading-tight">
              Speak with a certified <span className="italic gold-gradient">Vedic Astrologer.</span>
            </h3>
            <p className="text-[#A0A4AB] mt-5">
              Chat, voice or video sessions — from ₹30/min. All astrologers verified & rated.
            </p>
            <Link to="/astrologers" className="mt-8 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#D4AF37] text-black font-medium hover:bg-[#E5C865] transition">
              Talk to an Astrologer <ArrowUpRight size={16}/>
            </Link>
          </div>
          <div className="grid grid-cols-3 gap-3">
            {["Career","Marriage","Health","Finance","Education","Family"].map((t) => (
              <div key={t} className="p-4 rounded-lg bg-[#12151C] border border-white/10 text-center">
                <Sparkles size={14} className="mx-auto text-[#D4AF37]"/>
                <div className="text-xs uppercase tracking-widest mt-2 text-[#A0A4AB]">{t}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
